import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import clsx from "clsx";
import { ReactNode, useState, useId } from "react";

type MemoCollapseProps = {
    id?: string;
    title: string;
    children: ReactNode;
    defaultOpen?: boolean;
};

export function MemoCollapse({
    id,
    title,
    children,
    defaultOpen = false,
}: MemoCollapseProps) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const generatedId = useId();
    const panelId = id ? `${id}-panel` : `${generatedId}-panel`;

    return (
        <div id={id} className="w-full">
            <div
                onClick={() => setIsOpen((prev) => !prev)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                className={clsx(
                    "flex cursor-pointer items-center justify-between border-b py-6",
                    isOpen ? "border-b-0" : "border-slate-700",
                )}
            >
                <h2 className="display-s">{title}</h2>
                <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                >
                    <ChevronDownIcon className="size-5 shrink-0" />
                </motion.div>
            </div>
            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        id={panelId}
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                            open: {
                                opacity: 1,
                                height: "auto",
                                transition: {
                                    opacity: {
                                        duration: 0.6,
                                        ease: [0.04, 0.62, 0.23, 0.98],
                                    },
                                    height: {
                                        duration: 0.2,
                                        ease: [0.04, 0.62, 0.23, 0.98],
                                    },
                                },
                            },
                            collapsed: {
                                opacity: 0,
                                height: 0,
                                transition: {
                                    opacity: {
                                        duration: 0.2,
                                        ease: [0.04, 0.62, 0.23, 0.98],
                                    },
                                    height: {
                                        duration: 0.6,
                                        ease: [0.04, 0.62, 0.23, 0.98],
                                    },
                                },
                            },
                        }}
                        className="overflow-hidden border-b border-slate-700"
                    >
                        <div className="pb-8">{children}</div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
